import React from "react";
import { GraduationCap, IndianRupee } from "lucide-react";
import { Separator } from "../ui";
import { convertToIndianCurrency } from "../../utils/common";

/**
 * StudentCourseBanner — Full-width course + fee summary card.
 *
 * Props:
 *   student      — Student object with course, batch, totalFees, paidFees, pendingFees
 *   onCollectFee — Handler for "Collect" button
 *   onJoinClass  — Handler for "Join Class" button (shown when batch is live)
 *   readOnly     — Hide collect button
 */
const StudentCourseBanner = ({ student, courseAssignments = [], onCollectFee, onJoinClass, onPayNow, readOnly = false }) => {
  const batchStart = student.batch?.startDate
    ? new Date(student.batch.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })
    : "—";
  const batchEnd = student.batch?.endDate
    ? new Date(student.batch.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })
    : "—";

  const paid = parseFloat(student.paidFees) || 0;
  const pending = parseFloat(student.pendingFees) || 0;

  const additionalCourses = courseAssignments.filter(a => !a.isPrimary && a.status === 'active');

  return (
    <div>
      <div className="sp-course-banner">
      <div className="sp-course-banner-left">
        <div className="sp-course-banner-icon">
          <GraduationCap size={22} />
        </div>
        <div>
          <h3 className="sp-course-banner-title">{student.course?.name || "—"}</h3>
          <p className="sp-course-banner-sub">
            {student.batch?.name || "—"} &bull; {batchStart} - {batchEnd}
          </p>
        </div>
      </div>
      <div className="sp-course-banner-right">
        <div className="sp-course-banner-fee">
          <span className="sp-course-banner-fee-value sp-course-banner-fee--paid">
            {convertToIndianCurrency(paid)}
          </span>
          <span className="sp-course-banner-fee-label">Paid</span>
        </div>
        {pending > 0 && (
          <>
            <Separator orientation="vertical" className="sp-course-banner-sep" />
            <div className="sp-course-banner-fee">
              <span className="sp-course-banner-fee-value sp-course-banner-fee--pending">
                {convertToIndianCurrency(pending)}
              </span>
              <span className="sp-course-banner-fee-label">Pending</span>
            </div>
          </>
        )}
        {onJoinClass && (
          <button className="btn btn-sm sp-live-btn" onClick={onJoinClass}>
            <span className="sp-live-dot" />
            Join Class
          </button>
        )}
        {!readOnly && onCollectFee && (
          <button className="btn btn-primary btn-sm sp-course-banner-btn" onClick={onCollectFee}>
            <IndianRupee size={12} className="me-1" />
            Collect
          </button>
        )}
        {readOnly && pending > 0 && onPayNow && (
          <button className="btn btn-primary btn-sm sp-course-banner-btn" onClick={onPayNow}>
            <IndianRupee size={12} className="me-1" />
            Pay Now
          </button>
        )}
      </div>
    </div>

    {/* Additional Courses */}
    {additionalCourses.length > 0 && (
      <div className="mt-2">
        {additionalCourses.map((a) => (
          <div key={a._id} className="sp-course-banner" style={{ marginTop: 8, opacity: 0.85 }}>
            <div className="sp-course-banner-left">
              <div className="sp-course-banner-icon" style={{ background: 'var(--bg-tertiary, #f0f0f0)' }}>
                <GraduationCap size={18} />
              </div>
              <div>
                <h3 className="sp-course-banner-title" style={{ fontSize: '0.9rem' }}>{a.courseId?.name || "—"}</h3>
                <p className="sp-course-banner-sub">{a.batchId?.name || "No batch"}</p>
              </div>
            </div>
            <div className="sp-course-banner-right">
              <div className="sp-course-banner-fee">
                <span className="sp-course-banner-fee-value sp-course-banner-fee--paid" style={{ fontSize: '0.85rem' }}>
                  {convertToIndianCurrency(a.paidFees || 0)}
                </span>
                <span className="sp-course-banner-fee-label">Paid</span>
              </div>
              {(a.pendingFees || 0) > 0 && (
                <>
                  <Separator orientation="vertical" className="sp-course-banner-sep" />
                  <div className="sp-course-banner-fee">
                    <span className="sp-course-banner-fee-value sp-course-banner-fee--pending" style={{ fontSize: '0.85rem' }}>
                      {convertToIndianCurrency(a.pendingFees || 0)}
                    </span>
                    <span className="sp-course-banner-fee-label">Pending</span>
                  </div>
                </>
              )}
              {readOnly && a.batchId?.isLive && (
                <button className="btn btn-sm sp-live-btn" onClick={() => window.location.href = `/s/classroom/${a.batchId._id}`}>
                  <span className="sp-live-dot" />
                  Join Class
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
    </div>
  );
};

export default StudentCourseBanner;
